// iter496 — Smart Groups view for the bank-statement detail page.
// Auto-groups every row on a statement by inferred category (POS Sales,
// SPAN/VISA/Mastercard fees, VAT, SADAD bills, SARIE transfers, etc.),
// shows ONE row per group with running total, count, and date range,
// and offers a single-click "Add total to Expenses" button per group.
// iter544 — Adds (1) per-group branch picker so the user explicitly
// chooses which branch each Smart Group expense lands in, and (2) a
// per-row "Add as Expense" button so individual debit transactions can
// be booked one-at-a-time with their own branch.
import { cur } from '@/lib/currency';
import { useEffect, useState } from 'react';
import api from '../../lib/api';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Checkbox } from '../ui/checkbox';
import { Loader2, ChevronDown, ChevronRight, FilePlus, CheckCircle2, RefreshCw, BookmarkPlus, Play, PlayCircle, Trash2, Pause, Building2 } from 'lucide-react';

const GROUP_COLOR = {
  pos_sales: 'bg-emerald-50 border-emerald-200 text-emerald-800',
  master_card_fees: 'bg-rose-50 border-rose-200 text-rose-800',
  visa_fees: 'bg-indigo-50 border-indigo-200 text-indigo-800',
  span_fees: 'bg-amber-50 border-amber-200 text-amber-800',
  vat_fees: 'bg-cyan-50 border-cyan-200 text-cyan-800',
  sarie_charges: 'bg-slate-50 border-slate-200 text-slate-800',
  pos_fee_coll: 'bg-slate-50 border-slate-200 text-slate-800',
  sadad_bills: 'bg-violet-50 border-violet-200 text-violet-800',
  sadad_refunds: 'bg-violet-50 border-violet-200 text-violet-800',
  sarie_outgoing: 'bg-orange-50 border-orange-200 text-orange-800',
  sarie_incoming: 'bg-teal-50 border-teal-200 text-teal-800',
  internal_transfer: 'bg-zinc-50 border-zinc-200 text-zinc-800',
  salary: 'bg-pink-50 border-pink-200 text-pink-800',
  other: 'bg-gray-50 border-gray-200 text-gray-800',
};

const fmt = (n) => new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);

// iter552 — Categories that are ALWAYS expenses/fees (never sales).
// Even when the running total is positive (refunds/reversals), these
// MUST NOT show a "Tag as Sale" button — they show "Book as Fee Refund"
// instead so the books stay clean.
const FEE_KEYS = new Set([
  'pos_fees', 'pos_fee_coll', 'pos_monthly_fee',
  'master_card_fees', 'visa_fees', 'span_fees', 'mada_fees',
  'vat_fees', 'sarie_charges', 'bank_fees',
  'sadad_bills',
]);

export function SmartGroupsTab({ statementId, branchId, branches = [], onChange }) {
  const [loading, setLoading] = useState(true);
  const [groups, setGroups] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [busy, setBusy] = useState({});
  // iter497 — Persistent rules
  const [rules, setRules] = useState([]);
  const [rulesBusy, setRulesBusy] = useState(false);
  const [showRules, setShowRules] = useState(true);
  // iter544 — Per-group branch override (defaults to the statement's
  // branch). Stored as { [g.key]: branch_id }.
  const [groupBranch, setGroupBranch] = useState({});
  // iter544 — Per-row "Add as Expense" busy state.
  const [rowBusy, setRowBusy] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const [gR, rR] = await Promise.all([
        api.get(`/bank-statements/${statementId}/smart-groups`),
        api.get(`/banking/group-rules`, { params: { statement_id: statementId } }).catch(() => ({ data: { rules: [] } })),
      ]);
      setGroups(gR.data?.groups || []);
      setRules(rR.data?.rules || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load smart groups');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { if (statementId) load(); /* eslint-disable-next-line */ }, [statementId]);

  // iter552 — Replaced two back-to-back window.confirm() prompts with a
  // single proper dialog where the user can review/edit category, branch,
  // notes, and the "save as rule" toggle BEFORE posting the expense.
  const [confirmDlg, setConfirmDlg] = useState(null);  // { group, category, branch_id, notes, save_as_rule, is_refund }

  const openAddToExpense = (g, isRefund = false) => {
    const debitAmt = isRefund ? g.total_credit : g.total_debit;
    if (debitAmt <= 0) {
      toast.error(`${g.label} has no amount — not eligible for "Add to Expense".`);
      return;
    }
    setConfirmDlg({
      group: g,
      amount: debitAmt,
      category: g.suggested_category || g.key,
      branch_id: groupBranch[g.key] || branchId || '',
      notes: '',
      save_as_rule: false,
      is_refund: isRefund,
    });
  };

  const submitAddToExpense = async () => {
    if (!confirmDlg) return;
    const { group: g, category, branch_id, notes, save_as_rule, is_refund } = confirmDlg;
    setBusy(b => ({ ...b, [g.key]: true }));
    try {
      const r = await api.post(`/bank-statements/${statementId}/smart-groups/${g.key}/add-to-expense`, {
        branch_id: branch_id || undefined,
        category: category || undefined,
        description: notes ? `${g.label} — ${notes}` : undefined,
        is_refund: is_refund || undefined,
        save_as_rule,
      });
      toast.success(
        `Posted ${fmt(r.data?.expense?.amount || 0)} to Expenses, re-tagged ${r.data?.retagged || 0} rows.` +
        (r.data?.rule_saved_id ? ' Rule saved.' : '')
      );
      setConfirmDlg(null);
      load();
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Add to Expense failed');
    } finally {
      setBusy(b => ({ ...b, [g.key]: false }));
    }
  };

  // Kept for callsites that still want one-line "add to expense" — now just
  // opens the modal instead of two confirms.
  const addToExpense = (g) => openAddToExpense(g, g.is_refund || false);

  // iter497 — Rule controls
  const applyRule = async (ruleId) => {
    setRulesBusy(true);
    try {
      const r = await api.post(`/bank-statements/${statementId}/apply-rule/${ruleId}`);
      const matched = r.data?.matched || 0;
      if (matched === 0) {
        toast.info('No untagged rows match this rule on this statement.');
      } else {
        toast.success(`Rule applied: ${matched} rows tagged${r.data?.expense ? ` · expense ${fmt(r.data.expense.amount)} created` : ''}.`);
      }
      load();
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to apply rule');
    } finally {
      setRulesBusy(false);
    }
  };

  const applyAllRules = async () => {
    if (!rules.some(r => r.active && (r.matched_count_on_statement || 0) > 0)) {
      toast.info('No active rules with matching rows on this statement.');
      return;
    }
    if (!window.confirm(`Run every active rule against this statement now? Rules with 0 matches are skipped automatically.`)) return;
    setRulesBusy(true);
    try {
      const r = await api.post(`/bank-statements/${statementId}/apply-all-rules`);
      const results = r.data?.results || [];
      const summary = results.filter(x => x.matched > 0).map(x => `${x.rule_name}: ${x.matched}`).join(', ') || 'no matches';
      toast.success(`Ran ${results.length} rule${results.length === 1 ? '' : 's'}. ${summary}`);
      load();
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to apply rules');
    } finally {
      setRulesBusy(false);
    }
  };

  const toggleRule = async (rule) => {
    try {
      await api.patch(`/banking/group-rules/${rule.id}`, { active: !rule.active });
      toast.success(`Rule ${!rule.active ? 'enabled' : 'paused'}.`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to toggle rule');
    }
  };

  const deleteRule = async (rule) => {
    if (!window.confirm(`Delete rule "${rule.name}"? This does NOT undo any past applications — only future runs are removed.`)) return;
    try {
      await api.delete(`/banking/group-rules/${rule.id}`);
      toast.success('Rule deleted.');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to delete rule');
    }
  };

  const tagAsCategory = async (g, category) => {
    if (!g.txn_indexes?.length) return;
    if (!window.confirm(`Tag all ${g.count} "${g.label}" rows as ${category}?`)) return;
    setBusy(b => ({ ...b, [g.key]: true }));
    try {
      const r = await api.post(`/bank-statements/${statementId}/transactions/bulk-category`, {
        indexes: g.txn_indexes,
        category,
        branch_id: (groupBranch[g.key] || branchId) || undefined,
      });
      toast.success(`Tagged ${r.data?.updated || 0} rows as ${category}.`);
      load();
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Bulk-tag failed');
    } finally {
      setBusy(b => ({ ...b, [g.key]: false }));
    }
  };

  // iter544 — Book a single bank-statement debit row as one expense
  // entry. The branch is taken from the per-group override, falling
  // back to the statement-level branch. Asks the user to confirm
  // because this is destructive (re-tags the row).
  const addRowAsExpense = async (g, sample) => {
    if (!sample || (sample.debit || 0) <= 0) {
      toast.error('Row has no debit total — not bookable.');
      return;
    }
    const targetBranch = groupBranch[g.key] || branchId || '';
    const branchLabel = targetBranch
      ? (branches.find(b => b.id === targetBranch)?.name || 'selected branch')
      : 'no branch';
    if (!window.confirm(
      `Book this row as ONE expense of ${cur()} ${fmt(sample.debit)}?\n\n` +
      `Date: ${sample.date}\n` +
      `Description: ${(sample.description || '').slice(0, 140)}\n` +
      `Branch: ${branchLabel}\n\n` +
      `The row will be stamped so it won't appear unbooked again.`
    )) return;
    const key = `${g.key}::${sample.index}`;
    setRowBusy(b => ({ ...b, [key]: true }));
    try {
      const r = await api.post(`/bank-statements/${statementId}/transactions/${sample.index}/add-to-expense`, {
        branch_id: targetBranch || undefined,
      });
      if (r.data?.created) {
        toast.success(`Booked ${cur()} ${fmt(r.data.expense?.amount || sample.debit)} as expense`);
      } else {
        toast.info(`Already booked (${r.data?.reason || 'duplicate'})`);
      }
      load();
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Booking failed');
    } finally {
      setRowBusy(b => ({ ...b, [key]: false }));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 p-6 text-sm text-muted-foreground" data-testid="smart-groups-loading">
        <Loader2 size={14} className="animate-spin" /> Analysing statement…
      </div>
    );
  }
  if (!groups.length) {
    return <div className="p-6 text-sm text-muted-foreground" data-testid="smart-groups-empty">No transactions to group on this statement.</div>;
  }

  return (
    <div className="space-y-2" data-testid="smart-groups-tab">
      {/* iter497 — Saved Rules panel. Never auto-fires; explicit "Apply" only. */}
      {rules.length > 0 && (
        <div className="border border-violet-200 bg-violet-50/60 rounded-xl p-3 mb-2" data-testid="saved-rules-panel">
          <button
            type="button"
            className="w-full flex items-center justify-between gap-2 text-violet-900 hover:text-violet-700"
            onClick={() => setShowRules(s => !s)}
            data-testid="saved-rules-toggle"
          >
            <span className="flex items-center gap-1.5 font-semibold text-sm">
              <BookmarkPlus size={13} />
              Saved Rules ({rules.length}) · {rules.filter(r => (r.matched_count_on_statement || 0) > 0).length} apply to this statement
            </span>
            <span className="text-violet-500">{showRules ? '▾' : '▸'}</span>
          </button>
          {showRules && (
            <>
              <div className="flex items-center justify-between gap-2 mt-2 pb-2 border-b border-violet-200">
                <p className="text-[10px] text-muted-foreground">
                  Rules NEVER fire automatically. Click <strong>Apply</strong> on a single rule, or <strong>Apply all</strong> to run every active rule that matches untagged rows on this statement.
                </p>
                <Button
                  size="sm"
                  className="h-7 text-[11px] bg-violet-600 hover:bg-violet-700 text-white shrink-0"
                  onClick={applyAllRules}
                  disabled={rulesBusy || !rules.some(r => r.active && (r.matched_count_on_statement || 0) > 0)}
                  data-testid="saved-rules-apply-all"
                >
                  {rulesBusy ? <Loader2 size={11} className="animate-spin mr-1" /> : <PlayCircle size={11} className="mr-1" />}
                  Apply all
                </Button>
              </div>
              <div className="space-y-1 mt-2">
                {rules.map(r => {
                  const matched = r.matched_count_on_statement || 0;
                  return (
                    <div key={r.id} className="flex items-center gap-2 text-xs py-1 px-2 rounded bg-white/70" data-testid={`saved-rule-${r.id}`}>
                      <span className={`shrink-0 w-2 h-2 rounded-full ${r.active ? 'bg-emerald-500' : 'bg-slate-300'}`} title={r.active ? 'Active' : 'Paused'} />
                      <span className="font-medium flex-1 truncate" title={r.name}>{r.name}</span>
                      <Badge variant="outline" className="text-[10px]">{r.group_key}</Badge>
                      <Badge variant="outline" className="text-[10px]">→ {r.category}</Badge>
                      <Badge className={`text-[10px] ${matched > 0 ? 'bg-emerald-100 text-emerald-800 border-emerald-200' : 'bg-slate-100 text-slate-600 border-slate-200'}`}>
                        {matched} match{matched === 1 ? '' : 'es'}
                      </Badge>
                      <span className="text-[10px] text-muted-foreground">run {r.run_count || 0}×</span>
                      <Button
                        size="sm"
                        className="h-6 text-[10px] px-2 bg-violet-600 hover:bg-violet-700 text-white"
                        onClick={() => applyRule(r.id)}
                        disabled={rulesBusy || !r.active || matched === 0}
                        data-testid={`saved-rule-apply-${r.id}`}
                        title={!r.active ? 'Rule is paused' : matched === 0 ? 'No untagged rows match on this statement' : 'Apply now'}
                      >
                        <Play size={9} className="mr-0.5" /> Apply
                      </Button>
                      <button
                        type="button"
                        className="p-1 text-slate-600 hover:text-slate-900"
                        onClick={() => toggleRule(r)}
                        title={r.active ? 'Pause rule' : 'Activate rule'}
                        data-testid={`saved-rule-toggle-${r.id}`}
                      >
                        <Pause size={10} />
                      </button>
                      <button
                        type="button"
                        className="p-1 text-rose-600 hover:text-rose-800"
                        onClick={() => deleteRule(r)}
                        title="Delete rule"
                        data-testid={`saved-rule-delete-${r.id}`}
                      >
                        <Trash2 size={10} />
                      </button>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      )}

      <div className="flex items-center justify-between mb-1">
        <p className="text-xs text-muted-foreground max-w-xl">
          Auto-grouped by inferred category. Click a row to drill into the underlying transactions, or press <strong>"Add to Expense"</strong> to post the whole group's debit total as one expense line.
        </p>
        <Button size="sm" variant="ghost" onClick={load} className="h-7 text-xs">
          <RefreshCw size={12} className="mr-1" /> Refresh
        </Button>
      </div>

      {groups.map(g => {
        const isOpen = !!expanded[g.key];
        const cls = GROUP_COLOR[g.key] || GROUP_COLOR.other;
        const isCredit = g.total_credit > g.total_debit;
        return (
          <div key={g.key} className={`border rounded-xl ${cls}`} data-testid={`smart-group-${g.key}`}>
            <div className="flex items-center gap-2 p-3">
              <button
                type="button"
                onClick={() => setExpanded(e => ({ ...e, [g.key]: !e[g.key] }))}
                className="flex items-center gap-1 flex-1 text-left"
                data-testid={`smart-group-toggle-${g.key}`}
              >
                {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                <span className="font-semibold">{g.label}</span>
                <Badge variant="outline" className="ml-2 text-[10px] bg-white/60">{g.count} txns</Badge>
                {g.sub_groups?.length > 0 && <Badge variant="outline" className="text-[10px] bg-white/60">{g.sub_groups.length} sub</Badge>}
                <span className="text-[10px] text-muted-foreground ml-2">{g.first_date} → {g.last_date}</span>
              </button>
              <div className="text-right shrink-0">
                <div className={`font-bold text-sm ${isCredit ? 'text-success' : 'text-error'}`}>
                  {isCredit ? '+' : '−'}{fmt(Math.max(g.total_credit, g.total_debit))}
                </div>
                {g.total_credit > 0 && g.total_debit > 0 && (
                  <div className="text-[10px] text-muted-foreground">net {fmt(g.net)}</div>
                )}
              </div>
              {/* iter544 — Per-group branch picker. Defaults to the
                  statement's branch when set. Required before booking
                  the group so cross-branch expenses land correctly. */}
              {branches.length > 0 && g.total_debit > 0 && (
                <div className="flex items-center gap-1 shrink-0" title="Which branch should this group's expense land in?">
                  <Building2 size={11} className="text-muted-foreground" />
                  <select
                    value={groupBranch[g.key] ?? branchId ?? ''}
                    onChange={(e) => setGroupBranch(b => ({ ...b, [g.key]: e.target.value }))}
                    onClick={(e) => e.stopPropagation()}
                    className="h-7 max-w-[140px] rounded border bg-white/80 px-1 text-[11px] focus:border-emerald-500 focus:outline-none"
                    data-testid={`smart-group-branch-${g.key}`}
                  >
                    <option value="">— No branch —</option>
                    {branches.map(b => (
                      <option key={b.id} value={b.id}>{b.name}</option>
                    ))}
                  </select>
                </div>
              )}
              {g.total_debit > 0 && (
                g.booked_expense_id ? (
                  <Badge variant="outline" className="text-[10px] bg-emerald-100 text-emerald-700 border-emerald-300" data-testid={`smart-group-booked-${g.key}`} title="Already booked as an expense">
                    ✓ Booked
                  </Badge>
                ) : (
                <Button
                  size="sm"
                  className="h-7 text-xs bg-white/80 hover:bg-white text-rose-700 border border-rose-300"
                  onClick={() => addToExpense(g)}
                  disabled={!!busy[g.key]}
                  data-testid={`smart-group-add-expense-${g.key}`}
                  title="Post the group's debit total as one Expense entry"
                >
                  {busy[g.key] ? <Loader2 size={11} className="animate-spin mr-1" /> : <FilePlus size={11} className="mr-1" />} Add to Expense
                </Button>
                )
              )}
              {g.total_credit > 0 && !FEE_KEYS.has(g.key) && (
                <Button
                  size="sm"
                  className="h-7 text-xs bg-white/80 hover:bg-white text-emerald-700 border border-emerald-300"
                  onClick={() => tagAsCategory(g, g.key === 'pos_sales' ? 'pos_sales' : 'other_sales')}
                  disabled={!!busy[g.key]}
                  data-testid={`smart-group-tag-pos-${g.key}`}
                  title="Tag every row in this group as POS / Other sale"
                >
                  {busy[g.key] ? <Loader2 size={11} className="animate-spin mr-1" /> : <CheckCircle2 size={11} className="mr-1" />}
                  Tag as Sale
                </Button>
              )}
              {g.total_credit > 0 && FEE_KEYS.has(g.key) && (
                <Button
                  size="sm"
                  className="h-7 text-xs bg-white/80 hover:bg-white text-amber-700 border border-amber-300"
                  onClick={() => addToExpense({ ...g, total_debit: g.total_credit, is_refund: true })}
                  disabled={!!busy[g.key]}
                  data-testid={`smart-group-fee-refund-${g.key}`}
                  title="Fee refund / reversal — book as negative expense, never as Sale"
                >
                  {busy[g.key] ? <Loader2 size={11} className="animate-spin mr-1" /> : <FilePlus size={11} className="mr-1" />}
                  Book as Fee Refund
                </Button>
              )}
            </div>

            {isOpen && (
              <div className="border-t border-white/40 bg-white/40 p-3 space-y-2">
                {g.sub_groups?.length > 0 && (
                  <div className="space-y-1">
                    <div className="text-[11px] font-semibold uppercase text-muted-foreground">Sub-groups</div>
                    {g.sub_groups.map(sg => (
                      <div key={sg.key} className="flex items-center justify-between text-xs py-0.5 px-2 rounded bg-white/60">
                        <span className="font-mono">{sg.key}</span>
                        <Badge variant="secondary" className="text-[10px]">{sg.count}×</Badge>
                        <span className="font-semibold">
                          {sg.total_debit > 0 ? <span className="text-error">−{fmt(sg.total_debit)}</span> : <span className="text-success">+{fmt(sg.total_credit)}</span>}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
                <div className="space-y-0.5">
                  <div className="text-[11px] font-semibold uppercase text-muted-foreground">Sample transactions{g.count > g.samples.length ? ` (first ${g.samples.length} of ${g.count})` : ''}</div>
                  <div className="max-h-72 overflow-y-auto">
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead className="sticky top-0 bg-white/80">
                          <tr className="border-b">
                            <th className="text-left p-1 font-medium">Date</th>
                            <th className="text-left p-1 font-medium">Description</th>
                            <th className="text-right p-1 font-medium">Amount</th>
                            {g.total_debit > 0 && <th className="text-right p-1 font-medium w-24">Action</th>}
                          </tr>
                        </thead>
                        <tbody>
                          {g.samples.map(s => {
                            const rowKey = `${g.key}::${s.index}`;
                            const rowDebit = (s.debit || 0) > 0;
                            // iter546 — disable "Add" when this row is
                            // already linked to a downstream record.
                            const alreadyBooked = !!(s.expense_id || s.supplier_payment_id || s.sale_id);
                            return (
                              <tr key={s.index} className={`border-b border-white/30 hover:bg-white/40 ${alreadyBooked ? 'opacity-60' : ''}`}>
                                <td className="p-1 whitespace-nowrap">{s.date}</td>
                                <td className="p-1 text-[11px] truncate max-w-xs" title={s.description}>{s.description}{s.manual && <Badge variant="outline" className="ml-1 text-[9px] bg-emerald-100">manual</Badge>}{alreadyBooked && <Badge variant="outline" className="ml-1 text-[9px] bg-emerald-100 text-emerald-700 border-emerald-300">booked</Badge>}</td>
                                <td className="p-1 text-right font-mono">
                                  {s.credit > 0 ? <span className="text-success">+{fmt(s.credit)}</span> : <span className="text-error">−{fmt(s.debit)}</span>}
                                </td>
                                {g.total_debit > 0 && (
                                  <td className="p-1 text-right">
                                    {rowDebit ? (
                                      alreadyBooked ? (
                                        <span
                                          className="text-[10px] px-1.5 py-0.5 rounded border border-emerald-300 bg-emerald-50 text-emerald-700 inline-flex items-center gap-0.5"
                                          data-testid={`smart-group-row-booked-${g.key}-${s.index}`}
                                          title="Already booked as an expense / payment"
                                        >
                                          <CheckCircle2 size={9} /> Booked
                                        </span>
                                      ) : (
                                        <button
                                          type="button"
                                          onClick={() => addRowAsExpense(g, s)}
                                          disabled={!!rowBusy[rowKey]}
                                          className="text-[10px] px-1.5 py-0.5 rounded border border-rose-300 text-rose-700 hover:bg-rose-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors inline-flex items-center gap-0.5"
                                          data-testid={`smart-group-row-add-${g.key}-${s.index}`}
                                          title="Book this single row as an expense"
                                        >
                                          {rowBusy[rowKey] ? <Loader2 size={9} className="animate-spin" /> : <FilePlus size={9} />}
                                          <span>Add</span>
                                        </button>
                                      )
                                    ) : (
                                      <span className="text-[9px] text-muted-foreground">—</span>
                                    )}
                                  </td>
                                )}
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      })}

      {/* iter552 — Confirm dialog with editable category / branch / notes
          for "Add to Expense". Replaces the two back-to-back window.confirm
          prompts the user complained were "auto-adding without asking". */}
      <Dialog open={!!confirmDlg} onOpenChange={(o) => { if (!o) setConfirmDlg(null); }}>
        <DialogContent className="max-w-lg" data-testid="add-to-expense-dialog">
          <DialogHeader>
            <DialogTitle className="font-outfit flex items-center gap-2">
              <FilePlus size={16} />
              {confirmDlg?.is_refund ? 'Book Fee Refund' : 'Add to Expense'}
            </DialogTitle>
            {confirmDlg && (
              <DialogDescription>
                {confirmDlg.group.count} bank rows ({confirmDlg.group.first_date} → {confirmDlg.group.last_date}) will be posted as <strong>one</strong> expense line and re-tagged so the daily reconciler picks them up.
              </DialogDescription>
            )}
          </DialogHeader>

          {confirmDlg && (
            <div className="space-y-3">
              <div className={`p-3 rounded-lg border-2 ${confirmDlg.is_refund ? 'border-amber-300 bg-amber-50/50' : 'border-rose-300 bg-rose-50/50'}`}>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{confirmDlg.is_refund ? 'Refund amount' : 'Expense amount'}</div>
                <div className={`text-2xl font-bold tabular-nums ${confirmDlg.is_refund ? 'text-amber-700' : 'text-rose-700'}`}>
                  {confirmDlg.is_refund ? '+' : '−'}{fmt(confirmDlg.amount)}
                </div>
                <div className="text-xs text-muted-foreground mt-1">{confirmDlg.group.label}</div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="add-exp-category" className="text-xs">Category</Label>
                  <Input
                    id="add-exp-category"
                    value={confirmDlg.category}
                    onChange={e => setConfirmDlg({ ...confirmDlg, category: e.target.value })}
                    data-testid="add-to-expense-category"
                  />
                </div>
                <div>
                  <Label htmlFor="add-exp-branch" className="text-xs">Branch</Label>
                  <Select
                    value={confirmDlg.branch_id || '__none__'}
                    onValueChange={v => setConfirmDlg({ ...confirmDlg, branch_id: v === '__none__' ? '' : v })}
                  >
                    <SelectTrigger id="add-exp-branch" data-testid="add-to-expense-branch">
                      <SelectValue placeholder="No branch" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">— No branch —</SelectItem>
                      {branches.map(b => (
                        <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label htmlFor="add-exp-notes" className="text-xs">Notes (optional)</Label>
                <Textarea
                  id="add-exp-notes"
                  value={confirmDlg.notes}
                  onChange={e => setConfirmDlg({ ...confirmDlg, notes: e.target.value })}
                  placeholder="Any internal note about this batch…"
                  rows={2}
                  data-testid="add-to-expense-notes"
                />
              </div>

              <label className="flex items-center gap-2 text-xs cursor-pointer p-2 rounded bg-muted/40 border">
                <Checkbox
                  checked={confirmDlg.save_as_rule}
                  onCheckedChange={(c) => setConfirmDlg({ ...confirmDlg, save_as_rule: !!c })}
                  data-testid="add-to-expense-save-rule"
                />
                <span>
                  <strong>Save as a rule</strong> — future statements with similar rows will appear in "Saved rules" (never auto-fires).
                </span>
              </label>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDlg(null)} data-testid="add-to-expense-cancel">Cancel</Button>
            <Button
              onClick={submitAddToExpense}
              disabled={!!busy[confirmDlg?.group?.key]}
              data-testid="add-to-expense-confirm"
            >
              {busy[confirmDlg?.group?.key] ? <Loader2 size={14} className="animate-spin mr-1" /> : <FilePlus size={14} className="mr-1" />}
              {confirmDlg?.is_refund ? 'Book Refund' : 'Post Expense'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default SmartGroupsTab;
